export type OmdPrice = {
  value?: string;
  currency?: string;
  basis?: number;
  quantityUnit?: string;
};

export type OmdPicture = {
  url?: string;
  urlThumbnail?: string;
  description?: string;
  filename?: string;
  type?: string;
  use?: string;
  sortOrder?: number;
};

export type OmdDocument = {
  url?: string;
  urlThumbnail?: string;
  description?: string;
  filename?: string;
  type?: string;
  size?: number;
  sortOrder?: number;
};

export type OmdAttribute = {
  attributeName?: string;
  attributeValue1?: string;
  attributeValue2?: string;
  attributeUnit?: string;
  attributeSystem?: string;
};

export type OmdAccessory = {
  supplierPid?: string;
  manufacturerPid?: string;
  description?: string;
  /** Some wholesalers name accessories via productShortDescr instead of description. */
  productShortDescr?: string;
  thumbnailUrl?: string;
  necessaryForFunction?: boolean;
};

/** Reference to another article, e.g. additional.followupProduct on an expiring product. */
export type OmdRelatedProduct = {
  supplierPid?: string;
  manufacturerId?: string;
  manufacturerPid?: string;
  gtin?: number | string;
  productShortDescr?: string;
  description?: string;
  thumbnailUrl?: string;
};

export type OmdRawMaterial = {
  description?: string;
  value?: string;
  currency?: string;
  unit?: string;
};

export type OmdMeasure = {
  measure?: number | string;
  unit?: string;
};

export type OmdSparePartRow = {
  position?: string | number;
  text?: string;
  linkedProduct?: string;
  pricegroup?: string;
  articleRow?: boolean;
  textRow?: boolean;
};

export type OmdSparePartList = {
  listNumber?: string;
  rows?: OmdSparePartRow[];
};

export type OmdAdditional = {
  energyEfficiencyClass?: string;
  attribute?: OmdAttribute[];
  accessories?: OmdAccessory[];
  minOrderQuantity?: number | string;
  minOrderUnit?: string;
  constructionText?: string;
  deepLink?: string;
  /** Not boolean in practice: wholesalers send 'No' | 'Yes' | 'Yes-Successor'. */
  expiringProduct?: boolean | string;
  followupProduct?: OmdRelatedProduct | OmdRelatedProduct[];
  [key: string]: unknown;
};

export type OmdLogistics = {
  weight?: OmdMeasure;
  measureA?: OmdMeasure;
  measureB?: OmdMeasure;
  measureC?: OmdMeasure;
  countryOfOrigin?: string;
  standardDeliveryPeriod?: string | number;
  packagingQuantity?: number | string;
  commodityNumber?: string;
  weeeNumber?: string;
  lucidNumber?: string;
  packagingDisposalProvider?: string;
  reachInfo?: string;
  reachDate?: string;
  ubaListRelevant?: boolean;
  ubaListConform?: boolean;
  hazardousMaterial?: boolean;
  exportable?: boolean;
  [key: string]: unknown;
};

export type OmdProduct = {
  supplierPid?: string;
  manufacturerId?: string;
  manufacturerIdType?: string;
  manufacturerPid?: string;
  gtin?: number | string;
  basic?: {
    productShortDescr?: string;
    productType?: string;
    /** Some wholesalers send a list of series instead of a single value. */
    serie?: string | string[];
    modelNumber?: string;
    matchcode?: string;
    priceOnDemand?: boolean;
    startOfValidity?: string;
    noteOfUse?: string;
    commodityGroupDescr?: string;
    commodityGroupId?: string;
    mainCommodityGroupDescr?: string;
    mainCommodityGroupId?: string;
    rrp?: OmdPrice;
  };
  prices?: {
    taxCode?: number;
    listPrice?: OmdPrice;
    netPrice?: OmdPrice;
    rrp?: OmdPrice;
    billBasis?: string;
    rawMaterial?: OmdRawMaterial[];
  };
  descriptions?: {
    productDescr?: string;
    marketingText?: string;
    shorttext1?: string;
    shorttext2?: string;
  };
  additional?: OmdAdditional;
  logistics?: OmdLogistics;
  pictures?: OmdPicture[];
  documents?: OmdDocument[];
  sparepartlists?: OmdSparePartList[] | OmdSparePartList;
  [key: string]: unknown;
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  '2D': '2D-Draufsicht',
  '2F': '2D-Frontal',
  '2S': '2D-Seitenansicht',
  DB: 'Datenblatt',
  IS: 'Bedienungsanleitung',
  BS: 'Bedienungsanleitung',
  MA: 'Montageanleitung',
  TI: 'Technische Info',
  PP: 'Prospekt',
  EP: 'ErP-Produktdatenblatt',
  VI: 'Video',
  VP: 'Produktvideo',
  VM: 'Montagevideo',
  TZ: 'Maßzeichnung',
  SB: 'Schaltbild',
  CE: 'CE-Konformität',
  EL: 'Energielabel',
};

const PICTURE_TYPE_LABELS: Record<string, string> = {
  B_: 'Produktfoto',
  S_: 'S/W-Foto',
  U_: 'Strichzeichnung',
  V_: 'Vermaßte Zeichnung',
  X_: 'Explosionszeichnung',
  MI: 'Milieubild',
  DT: 'Detailansicht',
  KV: 'Keyvisual',
  LO: 'Logo',
  LS: 'Lifestyle',
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  STD: 'Standardartikel',
  ERA: 'Ersatzteil A',
  ERB: 'Ersatzteil B',
  ERC: 'Ersatzteil C',
  VA: 'Variante',
  MA: 'Maßanfertigung',
  DLS: 'Dienstleistung',
  PAK: 'Paket / Set',
  SON: 'Sonderartikel',
  KAL: 'Kalkulationsartikel',
  STG: 'Schüttgut',
};

const UNIT_LABELS: Record<string, string> = {
  PCE: 'Stück',
  MTR: 'Meter',
  KGM: 'Kilogramm',
  LTR: 'Liter',
  MTK: 'Quadratmeter',
  MTQ: 'Kubikmeter',
  SET: 'Satz',
  PR: 'Paar',
};

const TAX_CODE_LABELS: Record<number, string> = {
  0: 'Voller USt.-Satz',
  1: 'Halber USt.-Satz',
  7: '§13b UStG (Umkehr)',
  8: '§13b UStG (steuerfrei)',
};

const ENERGY_CLASS_STYLES: Record<string, { bg: string; text: string; border?: string }> = {
  'A+++': { bg: 'bg-emerald-800', text: 'text-white' },
  'A++': { bg: 'bg-emerald-700', text: 'text-white' },
  'A+': { bg: 'bg-emerald-600', text: 'text-white' },
  A: { bg: 'bg-green-500', text: 'text-white' },
  B: { bg: 'bg-lime-500', text: 'text-white' },
  C: { bg: 'bg-yellow-400', text: 'text-app-text' },
  D: { bg: 'bg-amber-500', text: 'text-white' },
  E: { bg: 'bg-orange-500', text: 'text-white' },
  F: { bg: 'bg-red-500', text: 'text-white' },
  G: { bg: 'bg-red-700', text: 'text-white' },
};

const KPI_ATTRIBUTE_PATTERNS: { label: string; patterns: RegExp[] }[] = [
  { label: 'Nennleistung', patterns: [/nennleistung/i, /heizleistung/i, /k\s*W$/i] },
  { label: 'COP', patterns: [/\bcop\b/i, /leistungszahl/i] },
  { label: 'Schallleistung', patterns: [/schall/i, /l\s*wa/i, /geräusch/i] },
  { label: 'Kältemittel', patterns: [/kältemittel/i, /refrigerant/i, /r32/i, /r410/i] },
];

export const OMD_PANEL_TABS = ['uebersicht', 'technik', 'dokumente', 'preise'] as const;
export type OmdPanelTabId = (typeof OMD_PANEL_TABS)[number];

export function asOmdProduct(value: unknown): OmdProduct | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as OmdProduct;
}

export function getOmdAttributes(product: OmdProduct): OmdAttribute[] {
  const attrs = product.additional?.attribute;
  return Array.isArray(attrs) ? attrs : [];
}

export function getOmdAccessories(product: OmdProduct): OmdAccessory[] {
  const items = product.additional?.accessories;
  return Array.isArray(items) ? items : [];
}

/** Wholesalers send followupProduct either as a single object or as a list. */
export function getOmdFollowupProducts(product: OmdProduct): OmdRelatedProduct[] {
  const raw = product.additional?.followupProduct;
  if (Array.isArray(raw)) return raw.filter((item) => item && typeof item === 'object');
  if (raw && typeof raw === 'object') return [raw];
  return [];
}

export function labelOmdRelatedProduct(item: OmdRelatedProduct): string {
  return (
    cleanOmdText(item.productShortDescr) ||
    cleanOmdText(item.description) ||
    cleanOmdText(item.manufacturerPid) ||
    cleanOmdText(item.supplierPid) ||
    'Artikel'
  );
}

export function getOmdSparePartLists(product: OmdProduct): OmdSparePartList[] {
  const raw = product.sparepartlists;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return [raw as OmdSparePartList];
  return [];
}

export function hasOmdAttributeData(product: OmdProduct): boolean {
  return getOmdAttributes(product).length > 0;
}

export function hasOmdDimensionsData(logistics?: OmdLogistics): boolean {
  if (!logistics) return false;
  return Boolean(
    formatMeasure(logistics.measureA) ||
      formatMeasure(logistics.measureB) ||
      formatMeasure(logistics.measureC) ||
      formatMeasure(logistics.weight),
  );
}

export function hasOmdComplianceData(product: OmdProduct): boolean {
  const log = product.logistics;
  if (!log) return false;
  const hasPills =
    log.hazardousMaterial != null || log.ubaListRelevant != null || log.exportable != null;
  return (
    hasPills ||
    Boolean(
      log.reachInfo ||
        formatOmdDate(log.reachDate) ||
        log.weeeNumber ||
        log.lucidNumber ||
        log.packagingDisposalProvider ||
        log.countryOfOrigin ||
        log.commodityNumber,
    )
  );
}

export function hasOmdSparePartData(product: OmdProduct): boolean {
  return getOmdSparePartLists(product).some((list) => (list.rows?.length ?? 0) > 0);
}

export function findAttributesByNames(product: OmdProduct, names: string[]): OmdAttribute[] {
  const attrs = getOmdAttributes(product);
  return names
    .map((name) => {
      const lower = name.toLowerCase();
      return attrs.find((a) => (a.attributeName || '').toLowerCase().includes(lower));
    })
    .filter((a): a is OmdAttribute => Boolean(a));
}

export function findKpiAttributes(product: OmdProduct): { label: string; value: string }[] {
  const attrs = getOmdAttributes(product);
  const results: { label: string; value: string }[] = [];
  for (const kpi of KPI_ATTRIBUTE_PATTERNS) {
    const match = attrs.find((a) => {
      const name = a.attributeName || '';
      return kpi.patterns.some((p) => p.test(name));
    });
    if (match) {
      const val = formatAttributeValue(match);
      if (val) results.push({ label: kpi.label, value: val });
    }
  }
  return results;
}

export function formatAttributeValue(attr: OmdAttribute): string {
  const parts = [attr.attributeValue1, attr.attributeValue2].filter(Boolean);
  const unit = attr.attributeUnit ? ` ${attr.attributeUnit}` : '';
  return parts.length ? `${parts.join(' ')}${unit}`.trim() : '';
}

export function energyClassStyles(energyClass: string | null | undefined): {
  bg: string;
  text: string;
  border?: string;
} {
  if (!energyClass) return { bg: 'bg-emerald-700', text: 'text-white' };
  const normalized = energyClass.trim().toUpperCase().replace(/\s+/g, '');
  return ENERGY_CLASS_STYLES[normalized] ?? { bg: 'bg-slate-600', text: 'text-white' };
}

export function taxCodeToVatPercent(code: number | undefined): number {
  if (code === 0 || code === 1) return 19;
  if (code === 7 || code === 8) return 0;
  return 19;
}

export function parseOmdPriceNumber(price?: OmdPrice | null): number | null {
  if (!price?.value) return null;
  const num = Number(String(price.value).replace(',', '.'));
  return Number.isFinite(num) ? num : null;
}

export function findDatenblattDocument(product: OmdProduct): OmdDocument | null {
  return (product.documents ?? []).find((d) => d.type === 'DB' && d.url) ?? null;
}

export function formatMeasure(m?: OmdMeasure | null): string | null {
  if (m?.measure == null || m.measure === '') return null;
  const unit = m.unit ? ` ${m.unit}` : '';
  return `${m.measure}${unit}`;
}

export function formatBoolDe(value: boolean | undefined | null): string {
  if (value == null) return '-';
  return value ? 'Ja' : 'Nein';
}

export function cleanOmdText(text: string | undefined | null): string {
  if (!text) return '';
  return text
    .replace(/^["']+|["']+$/g, '')
    .replace(/"{2,}/g, '"')
    .trim();
}

export function formatOmdPrice(price?: OmdPrice | null): string | null {
  if (!price?.value) return null;
  const num = Number(String(price.value).replace(',', '.'));
  const currency = normalizeOmdCurrency(price.currency);
  const formatted = Number.isFinite(num)
    ? formatOmdCurrencyAmount(num, currency)
    : `${price.value} ${currency}`;
  return `${formatted}${formatOmdPriceBasisSuffix(price)}`;
}

function normalizeOmdCurrency(code: string | undefined | null): string {
  const raw = String(code ?? '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(raw)) {
    try {
      new Intl.NumberFormat('de-DE', { style: 'currency', currency: raw }).format(0);
      return raw;
    } catch {
      /* invalid ISO 4217 code from OMD */
    }
  }
  return 'EUR';
}

function formatOmdCurrencyAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toLocaleString('de-DE')} ${currency}`;
  }
}

function formatOmdPriceBasisSuffix(price: OmdPrice): string {
  const unit = price.quantityUnit ? UNIT_LABELS[price.quantityUnit] || price.quantityUnit : '';
  if (price.basis && price.basis !== 1) {
    return ` je ${price.basis}${unit ? ` ${unit}` : ''}`;
  }
  return unit ? ` / ${unit}` : '';
}

/** Brutto-UVP aus Listenpreis + MwSt.; fallback auf OMD-RRP wenn kein Listenpreis. */
export function formatOmdUvpFromListPrice(product: OmdProduct): string | null {
  const listPrice = product.prices?.listPrice;
  const listNum = parseOmdPriceNumber(listPrice);
  if (listNum == null) {
    return formatOmdPrice(product.prices?.rrp ?? product.basic?.rrp);
  }

  const vatPercent = taxCodeToVatPercent(product.prices?.taxCode);
  const gross = listNum * (1 + vatPercent / 100);
  const currency = normalizeOmdCurrency(listPrice?.currency);
  const formatted = formatOmdCurrencyAmount(gross, currency);
  return `${formatted}${formatOmdPriceBasisSuffix(listPrice!)}`;
}

export function formatOmdDate(value: string | undefined | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(d);
}

export function formatOmdFileSize(bytes: number | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function labelDocumentType(type: string | undefined): string {
  if (!type) return 'Dokument';
  return DOCUMENT_TYPE_LABELS[type] || type;
}

export function labelPictureType(type: string | undefined): string {
  if (!type) return 'Bild';
  return PICTURE_TYPE_LABELS[type] || type;
}

export function labelProductType(type: string | undefined): string {
  if (!type) return '-';
  return PRODUCT_TYPE_LABELS[type] || type;
}

export function labelTaxCode(code: number | undefined): string | null {
  if (code == null) return null;
  return TAX_CODE_LABELS[code] || `Steuercode ${code}`;
}

export function isPdfUrl(url: string, filename?: string): boolean {
  const lower = `${url} ${filename || ''}`.toLowerCase();
  return lower.includes('.pdf') || lower.includes('application/pdf');
}

export function isImageUrl(url: string, filename?: string): boolean {
  const lower = `${url} ${filename || ''}`.toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(lower);
}

/** Extensions the browser cannot preview; navigating the URL starts a download. */
const OMD_DOWNLOAD_ONLY_EXTENSIONS = new Set([
  'dwg',
  'dxf',
  'dgn',
  'zip',
  'rar',
  '7z',
  'xls',
  'xlsx',
  'xlsm',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'step',
  'stp',
  'igs',
  'iges',
  'ifc',
  'rvt',
  'rfa',
  'obj',
  'stl',
  'sat',
  'ipt',
  'iam',
]);

export function omdDocumentFileExtension(url?: string, filename?: string): string {
  const fromName = (filename ?? '').trim().toLowerCase();
  const nameMatch = fromName.match(/\.([a-z0-9]{1,8})$/);
  if (nameMatch?.[1]) return nameMatch[1];
  try {
    const path = new URL(url ?? '', 'https://omd.local').pathname.toLowerCase();
    const pathMatch = path.match(/\.([a-z0-9]{1,8})$/);
    if (pathMatch?.[1]) return pathMatch[1];
  } catch {
    /* ignore invalid URL */
  }
  return '';
}

/** True when the file type itself cannot be shown inline (CAD, Office, ZIP, …). */
export function omdDocumentForcesDownload(doc: Pick<OmdDocument, 'url' | 'filename'>): boolean {
  const ext = omdDocumentFileExtension(doc.url, doc.filename);
  return Boolean(ext) && OMD_DOWNLOAD_ONLY_EXTENSIONS.has(ext);
}

const OMD_VIDEO_DOCUMENT_TYPES = new Set(['VI', 'VP', 'VM']);

/** OMD document types that are typically PDFs (URLs often omit a .pdf suffix). */
const OMD_PDF_DOCUMENT_TYPES = new Set([
  'DB',
  'EL',
  'EP',
  'IS',
  'BS',
  'MA',
  'TI',
  'PP',
  'TZ',
  'SB',
  'CE',
]);

export function isOmdPdfDocument(doc: Pick<OmdDocument, 'url' | 'filename' | 'type'>): boolean {
  const url = doc.url ?? '';
  if (!url) return false;
  if (isImageUrl(url, doc.filename)) return false;
  if (omdDocumentForcesDownload(doc)) return false;
  if (doc.type && OMD_VIDEO_DOCUMENT_TYPES.has(doc.type)) return false;
  if (isPdfUrl(url, doc.filename)) return true;
  if (doc.type && OMD_PDF_DOCUMENT_TYPES.has(doc.type)) return true;
  return false;
}

function trimTrailingHtmlBreaks(html: string): string {
  let result = html.trimEnd();
  let prev = '';
  while (result !== prev) {
    prev = result;
    result = result
      .replace(/(\s*<br\s*\/?>\s*)+$/gi, '')
      .replace(/(\s*<p>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>\s*)*<\/p>\s*)+$/gi, '')
      .replace(/(\s*<div>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>\s*)*<\/div>\s*)+$/gi, '')
      .trimEnd();
  }
  return result;
}

function trimTrailingBreakNodes(html: string): string {
  if (typeof window === 'undefined') return trimTrailingHtmlBreaks(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div data-omd-root="1">${html}</div>`, 'text/html');
  const root = doc.querySelector('[data-omd-root]');
  if (!root) return trimTrailingHtmlBreaks(html);

  const isEmptyBlock = (el: Element): boolean => {
    const tag = el.tagName.toLowerCase();
    if (tag !== 'p' && tag !== 'div') return false;
    if (el.querySelector('img,video,table,ul,ol,iframe')) return false;
    const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
    if (text) return false;
    return !el.querySelector('*:not(br)');
  };

  let changed = true;
  while (changed && root.lastChild) {
    changed = false;
    const last = root.lastChild;
    if (last.nodeType === Node.TEXT_NODE) {
      if (!(last.textContent ?? '').trim()) {
        root.removeChild(last);
        changed = true;
        continue;
      }
      break;
    }
    if (last.nodeType === Node.ELEMENT_NODE) {
      const el = last as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === 'br') {
        root.removeChild(last);
        changed = true;
        continue;
      }
      if (isEmptyBlock(el)) {
        root.removeChild(last);
        changed = true;
        continue;
      }
    }
    break;
  }

  return trimTrailingHtmlBreaks(root.innerHTML);
}

export function sanitizeOmdHtml(html: string): string {
  if (!html.trim()) return '';

  const pretrimmed = trimTrailingHtmlBreaks(html);

  if (typeof window === 'undefined') return pretrimmed;

  const parser = new DOMParser();
  const doc = parser.parseFromString(pretrimmed, 'text/html');
  doc.querySelectorAll('script,style,iframe,object,embed').forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const val = attr.value.trim().toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      if ((name === 'href' || name === 'src') && val.startsWith('javascript:')) el.removeAttribute(attr.name);
      if (name === 'style') {
        const cleaned = attr.value
          .replace(/(?:^|;)\s*(?:max-)?width\s*:\s*[^;]+/gi, '')
          .replace(/(?:^|;)\s*float\s*:\s*[^;]+/gi, '')
          .replace(/;\s*;/g, ';')
          .trim()
          .replace(/^;|;$/g, '');
        if (cleaned) el.setAttribute('style', cleaned);
        else el.removeAttribute('style');
      }
    }
  });
  return trimTrailingBreakNodes(doc.body.innerHTML);
}

/** Full-width flowing description text (OMD often uses &lt;br&gt; instead of paragraphs). */
export const OMD_DESCRIPTION_HTML_CLASS =
  'w-full min-w-0 text-sm leading-relaxed text-app-text-muted break-words [&_*]:max-w-full [&_table]:w-full [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0';

export function normalizeOmdDescriptionHtml(html: string): string {
  let safe = sanitizeOmdHtml(html);
  if (!safe.trim()) return '';

  safe = safe
    .replace(/(\s*<br\s*\/?>\s*){2,}/gi, '</p><p>')
    .replace(/\s*<br\s*\/?>\s*/gi, ' ')
    .replace(/\s{2,}/g, ' ');

  if (!/<(?:p|div|ul|ol|table|h[1-6])\b/i.test(safe)) {
    safe = `<p>${safe.trim()}</p>`;
  }

  return trimTrailingHtmlBreaks(safe);
}

export function hasOmdDescriptionHtml(html: string | undefined | null): boolean {
  if (!html?.trim()) return false;
  const normalized = normalizeOmdDescriptionHtml(html);
  if (typeof window === 'undefined') {
    return normalized.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length > 0;
  }
  const doc = new DOMParser().parseFromString(normalized, 'text/html');
  return Boolean((doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim());
}

export function sortByOrder<T extends { sortOrder?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
}

const ENERGY_CLASS_RE = /^A\+{0,3}$|^[B-G]$/i;

function findEnergyClassInValue(value: unknown, depth = 0): string | null {
  if (depth > 5 || value == null) return null;
  if (typeof value === 'string') {
    const normalized = value.trim().replace(/\s+/g, '');
    return ENERGY_CLASS_RE.test(normalized) ? normalized.toUpperCase() : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findEnergyClassInValue(item, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof value === 'object') {
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      const keyLower = key.toLowerCase();
      if (
        keyLower.includes('energy') ||
        keyLower.includes('erp') ||
        keyLower.includes('efficiency') ||
        keyLower.includes('effizienz')
      ) {
        if (typeof val === 'string' && val.trim()) {
          const normalized = val.trim().replace(/\s+/g, '');
          if (ENERGY_CLASS_RE.test(normalized)) return normalized.toUpperCase();
        }
      }
      const found = findEnergyClassInValue(val, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

export function extractOmdEnergyClass(product: OmdProduct): string | null {
  const direct = product.additional?.energyEfficiencyClass;
  if (direct && String(direct).trim()) {
    const normalized = String(direct).trim().replace(/\s+/g, '').toUpperCase();
    if (ENERGY_CLASS_RE.test(normalized)) return normalized;
    return String(direct).trim();
  }
  return findEnergyClassInValue(product.additional) ?? findEnergyClassInValue(product.basic);
}
